// src/features/system/user/components/UsersTable.tsx
import React, { useMemo, useState } from 'react';
import MinimalisticTable, { ColumnDef } from '@/shared/components/ui/MinimalisticTable';
import { useUsers } from '../hooks/useUsers';
import type { User } from '../api/usersApi';
import CreateUserDialog from './CreateUserDialog';
import { useTenants } from '@/features/system/tenants/hooks/useTenants';

type Row = User & { tenant_label: string };

export default function UsersTable() {
  const { data: users = [], isLoading, isError, error } = useUsers();
  const [open, setOpen] = useState(false);

  // tenants → map id_tenant -> tenant_name
  const { data: tenants = [], isLoading: tLoading, isError: tErr } = useTenants();
  const tenantNameById = useMemo(() => {
    const m = new Map<number, string>();
    tenants.forEach(t => m.set(t.id_tenant, t.tenant_name));
    return m;
  }, [tenants]);

  // rows with "tenant_name [#id]" (or fallbacks)
  const rows: Row[] = useMemo(
    () =>
      users.map(u => {
        if (tLoading) return { ...u, tenant_label: 'Loading…' };
        if (u.tenant_id == null) return { ...u, tenant_label: '—' };

        const name = tenantNameById.get(u.tenant_id);
        const label = name ? `${name} [#${u.tenant_id}]` : `[#${u.tenant_id}]`;
        return { ...u, tenant_label: label };
      }),
    [users, tenantNameById, tLoading]
  );

  const columns: ColumnDef<Row>[] = [
    { header: 'ID',       accessor: 'id_user'  },
    { header: 'Username', accessor: 'username' },
    { header: 'Tenant',   accessor: 'tenant_label' },
  ];

  if (isLoading) return <p className="text-neutral-400">Loading…</p>;
  if (isError)   return <p className="text-rose-400">Error: {(error as Error)?.message}</p>;
  if (tErr)      return <p className="text-rose-400">Error loading tenants.</p>;

  return (
    <>
      <MinimalisticTable
        columns={columns}
        data={rows}
        noDataMessage="No users."
        showSearch
        showNewButton
        onNew={() => setOpen(true)}
      />
      <CreateUserDialog open={open} onOpenChange={setOpen} />
    </>
  );
}
